/* @flow */

import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import { DocumentList } from 'components/DocumentList/DocumentList';
import RequireForAccess from 'react-access';
import { uploadDocumentsOrResources } from 'permissions/account';
import Button from '@material-ui/core/Button';
import { observable } from 'mobx';
import Snackbar from '@material-ui/core/Snackbar';

import { apiService } from 'services/ServiceRegistry';
import Loading from 'components/Loading/Loading';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import UserStore from 'stores/UserStore';
import AccountStore from 'stores/AccountStore';
import AssetStore from 'stores/AssetStore';

import styles from './DocumentsAndResources.module.scss';

type InjectedProps = {
  userStore: UserStore,
  accountStore: AccountStore,
  assetStore: AssetStore,
} & TranslatorProps;

@translate()
@inject('accountStore', 'assetStore', 'userStore')
@observer
export default class DocumentsAndResources extends ComponentWithInjectedProps<
  {},
  {},
  InjectedProps
> {
  @observable localState = {
    isLoading: false,
    showResponseMessage: false,
    responseMessage: '',
  };

  myUploadid = React.createRef();

  async componentDidMount() {
    if (this.props.accountStore.accountId) {
      this.localState.isLoading = true;
      await this.props.accountStore.loadDocumentsAndResources(this.props.accountStore.accountId);
      this.localState.isLoading = false;
    }
  }

  handleInputChange = (selectorFiles: FileList) => {
    const { t } = this.props;

    const vHeaders = new Headers();

    this.localState.responseMessage = 'Uploading...';
    this.localState.showResponseMessage = true;

    const vFormdata = new FormData();
    vFormdata.append('file', selectorFiles[0]);
    vFormdata.append('file_name', selectorFiles[0].name);

    apiService
      .createFileResource(
        `accounts/${this.props.accountStore.accountId}/documents/store-file`,
        vFormdata,
        vHeaders
      )
      .then((response: Object) => {
        this.localState.responseMessage = t('general:messages.file_upload_success');
        this.localState.showResponseMessage = true;
        this.props.accountStore.loadDocumentsAndResources(this.props.accountStore.accountId);
        return response;
      })

      .catch((err: string) => {
        this.localState.responseMessage = t('general:messages.file_upload_error');
        this.localState.showResponseMessage = true;
      });
  };

  handleMyUploadId() {
    this.myUploadid.current.click();
  }

  renderUploadButton() {
    const {
      t,
      userStore: { isReadOnly },
    } = this.props;

    return (
      <RequireForAccess permissions={uploadDocumentsOrResources}>
        <div className={styles['button-container']}>
          <input
            type="file"
            ref={this.myUploadid}
            id="myUploadid"
            style={{ visibility: 'hidden' }}
            onChange={(e: *) => this.handleInputChange(e.target.files)}
          />
          {
            <Button
              variant="contained"
              color="primary"
              disabled={isReadOnly}
              onClick={() => this.handleMyUploadId()}
            >
              {t('general:actions.upload')}
            </Button>
          }
        </div>
      </RequireForAccess>
    );
  }

  renderDocumentList() {
    const documentsAndResources = this.props.accountStore.getDocumentsAndResources;

    return this.localState.isLoading ? (
      <div className="loading-container">
        <Loading variant="small" />
      </div>
    ) : (
      <DocumentList listItems={documentsAndResources} />
    );
  }

  renderNoResults() {
    return (
      <p className={styles['no-results-container']}>
        {this.props.t('general:search_results.no_results_found')}
      </p>
    );
  }

  renderSnackBar() {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={this.localState.showResponseMessage}
        onClose={() => {
          this.localState.showResponseMessage = false;
        }}
        ContentProps={{
          'aria-describedby': 'message-id',
        }}
        message={<span id="message-id">{this.localState.responseMessage}</span>}
      />
    );
  }

  render() {
    const {
      t,
      accountStore: { getDocumentsAndResources: documentsAndResources },
    } = this.props;

    return (
      <React.Fragment>
        <div className={styles['documents-resources']}>
          <h3>{t('general:labels.documents_resources')}</h3>
          {this.renderUploadButton()}
        </div>

        {documentsAndResources.length > 0 ? this.renderDocumentList() : this.renderNoResults()}

        {this.renderSnackBar()}
      </React.Fragment>
    );
  }
}
